import React, { lazy, Suspense } from 'react';
import { Switch, Route, withRouter } from 'react-router-dom';

import { retry } from '@helpers';

import { Spinner } from 'styles/common';
import FeedbackModal from 'components/Feedback/Modal';

import ModalRoute from './ModalRoute';
import PrivateRoute from './PrivateRoute';
import PrivateModalRoute from './PrivateModalRoute';

const Home = lazy(() =>
	retry(() => import(/* webpackChunkName: "home" */ 'pages/home')),
);
const Login = lazy(() =>
	retry(() => import(/* webpackChunkName: "login" */ 'pages/login')),
);
const Order = lazy(() =>
	retry(() => import(/* webpackChunkName: "order" */ 'pages/order')),
);
const Company = lazy(() =>
	retry(() => import(/* webpackChunkName: "feedback" */ 'pages/company')),
);
const Register = lazy(() =>
	retry(() => import(/* webpackChunkName: "feedback" */ 'pages/register')),
);
const User = lazy(() =>
	retry(() => import(/* webpackChunkName: "user" */ 'pages/user')),
);
const Landing = lazy(() =>
	retry(() => import(/* webpackChunkName: "landing" */ 'pages/landing')),
);

const routes = [
	{
		path: '/',
		exact: true,
		render: ({ history, location }) => (
			<Home history={history} location={location} />
		),
	},
	{
		path: ['/signin/:step', '/signin'],
		render: () => <Login />,
	},
	{
		path: ['/company/:domain', '/company'],
		render: () => <Company />,
	},
	{
		path: '/register',
		render: () => <Register />,
	},
	{
		path: ['/menu/:dayId', '/menu'],
		private: true,
		component: Order,
	},
	{
		path: '/user',
		private: true,
		strict: true,
		component: User,
	},
	{
		path: ['/feedback/:invoiceId/:step', '/feedback/:invoiceId'],
		private: true,
		modal: true,
		component: FeedbackModal,
		dialog: {
			size: 'md',
		},
	},
	{
		path: '/landing/:company',
		exact: true,
		render: () => <Landing />,
	},
];

const Routes = ({ location }) => {
	const routeGenerator = (modal = undefined) => {
		return routes.map((route, i) => {
			if (modal !== route.modal) return null;

			// conditions
			const conditions = {};
			if (route.exact) conditions.exact = true;
			if (route.strict) conditions.strict = true;

			// modal OR private OR privateModal
			if (route.modal && route.private) {
				route.render = (props) => (
					<PrivateModalRoute
						component={route.component}
						dialogAttr={route.dialog}
						{...props}
						key={i}
					/>
				);
			} else if (route.modal) {
				route.render = (props) => (
					<ModalRoute
						component={route.component}
						dialogAttr={route.dialog}
						{...props}
						key={i}
					/>
				);
			} else if (route.private) {
				route.render = (props) => (
					<PrivateRoute component={route.component} {...props} key={i} />
				);
			}

			// generate routes
			return (
				<Route
					key={i}
					{...conditions}
					path={route.path}
					render={route.render}
				/>
			);
		});
	};

	const background = location.state && location.state.background;

	return (
		<Suspense fallback={<Spinner $size='lg' $block $margin={300} />}>
			<Switch location={background || location}>{routeGenerator()}</Switch>

			{background && routeGenerator(true)}
		</Suspense>
	);
};

export default withRouter(Routes);
